import { flightRecorder } from "@/services/debug/FlightRecorder";
import { PerformanceMetricsService } from "@/services/PerformanceMetricsService";
import { NetworkMetricsService } from "./NetworkMetricsService";
import { RenderMetricsService } from "./RenderMetricsService";
import type { PerfRollup } from "./types";

/** Product actions worth shipping externally; keeps {@link PerfRollup} payloads small and stable. */
const ROLLUP_ACTION_ALLOWLIST = [
  "bootstrap.full",
  "bootstrap.load",
  // Every paged local write shares this action name, so its rollup row summarizes the total
  // bootstrap local-write cost (count/avg/max) — the DB-write side of a cold boot.
  "bootstrap.dbwrite",
  "warmup.full",
  "feed.navigate",
  "feed.initial",
] as const;

/**
 * Single facade over every perf collector (compute, action, render, network), producing the compact,
 * synchronous {@link PerfRollup} for periodic external reporting. Nothing here is on a hot path —
 * `rollup` runs on the reporter's cadence. Local, detailed inspection is the flight recorder's.
 */
class PerfHubImpl {
  private readonly startedAt = Date.now();

  public rollup(reason: string, context?: Record<string, string | undefined>): PerfRollup {
    const actionSummary = PerformanceMetricsService.getActionSummarySync();
    const { fullMs, loadMs } = this.bootstrapDurations(actionSummary);
    const renderTotals = RenderMetricsService.getTotals();
    const networkTotals = NetworkMetricsService.getTotals();

    const actions: PerfRollup["actions"] = {};
    for (const action of ROLLUP_ACTION_ALLOWLIST) {
      const s = actionSummary[action];
      if (s) actions[action] = { count: s.count, avgMs: s.count ? s.totalMs / s.count : 0, maxMs: s.maxMs };
    }

    return {
      capturedAt: Date.now(),
      uptimeMs: Date.now() - this.startedAt,
      reason,
      context,
      bootstrapFullMs: fullMs,
      bootstrapLoadMs: loadMs,
      query: flightRecorder.computeTotals(),
      render: renderTotals,
      network: networkTotals,
      actions,
    };
  }

  public reset(): void {
    RenderMetricsService.reset();
    NetworkMetricsService.reset();
    flightRecorder.resetComputeTotals();
    void PerformanceMetricsService.clearMetrics();
  }

  private bootstrapDurations(actionSummary: ReturnType<typeof PerformanceMetricsService.getActionSummarySync>): {
    fullMs: number | null;
    loadMs: number | null;
  } {
    return {
      fullMs: actionSummary["bootstrap.full"]?.lastMs ?? null,
      loadMs: actionSummary["bootstrap.load"]?.lastMs ?? null,
    };
  }
}

export const PerfHub = new PerfHubImpl();
